import React, { useCallback, useEffect, useState, useRef } from "react";
import styled from "@emotion/styled";
import BackIcon from "@assets/icons/back.svg";
import { useI18n } from "@core/i18n/i18n";
import { useGoBack } from "@core/routing/use-navigate";
import { useParams } from "react-router-dom";
import { learnManager } from "@core/service-container";
import { LearnItem } from "@domain/learn/learn-service";
import { sendAnalyticsEventContentViewed } from "@domain/learn/learn-analytics";
import { physioMeTabletBreakpoint } from "@ui/style";
import { DocumentView } from "@ui/components/learn/document-view";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import ScrollToTop from "@ui/components/scroll-to-top/ScrollToTop";

export const LearnDocumentPage = (): JSX.Element => {
	const { format } = useI18n();
	const goBack = useGoBack();
	const { documentId } = useParams<{ documentId: string }>();

	const [item, setItem] = useState<LearnItem | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const maxScrollPercentageRef = useRef<number>(0);
	const calculateScrollPercentage = useCallback((): number => {
		const scrollTop = window.scrollY;
		const docHeight = document.documentElement.scrollHeight - window.innerHeight;
		return docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
	}, []);

	const handleRetry = useCallback(() => {
		if (!documentId) return;
		setLoading(true);
		setError(null);

		void learnManager
			.getItem(documentId)
			.then((document: LearnItem) => {
				setItem(document);
			})
			.catch((e: unknown) => {
				console.error("[LearnDocumentPage] Failed to load document", e);
				setError("Failed to load document. Please try again.");
			})
			.finally(() => {
				setLoading(false);
			});
	}, [documentId]);

	useEffect(() => {
		let mounted = true;
		if (!documentId) {
			setError("No document ID provided");
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(null);

		void learnManager
			.getItem(documentId)
			.then((document: LearnItem) => {
				if (!mounted) return;
				setItem(document);
			})
			.catch((e: unknown) => {
				console.error("[LearnDocumentPage] Failed to load document", e);
				if (mounted) {
					setError("Failed to load document. Please try again.");
				}
			})
			.finally(() => {
				if (mounted) setLoading(false);
			});

		return () => {
			mounted = false;
		};
	}, [documentId]);

	useEffect(() => {
		const handleScroll = () => {
			const currentPercent = calculateScrollPercentage();
			if (currentPercent > maxScrollPercentageRef.current) {
				maxScrollPercentageRef.current = currentPercent;
			}
		};
		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [calculateScrollPercentage]);

	useEffect(
		() => () => {
			if (item && documentId) {
				const percentValue = maxScrollPercentageRef.current;
				sendAnalyticsEventContentViewed(item.mediaType, documentId, percentValue);
			}
		},
		[item, documentId],
	);

	return (
		<PageWrapper>
			<HeaderSection>
				<BackButton as="button" onClick={goBack} aria-label={format("global.dialog.back")} type="button">
					<BackIcon width={20} height={20} aria-hidden="true" />
					<BackLabel>{format("global.dialog.back")}</BackLabel>
				</BackButton>
				<PageTitle>{format("tabbar.learn")}</PageTitle>
			</HeaderSection>

			<ContentContainer>
				<ContentCard>
					{loading && (
						<LoadingMessage role="status" aria-live="polite">
							<LoadingSpinner />
							<LoadingText>Loading content...</LoadingText>
						</LoadingMessage>
					)}

					{error && (
						<ErrorMessage role="alert" aria-live="assertive">
							<ErrorTitle>Unable to Load Content</ErrorTitle>
							<ErrorText>{error}</ErrorText>
							<RetryButton onClick={handleRetry}>Try Again</RetryButton>
						</ErrorMessage>
					)}

					{!loading && !error && item && (
						<article>
							<DocumentView item={item} />
						</article>
					)}
				</ContentCard>
			</ContentContainer>
			<ScrollToTop inline/>
		</PageWrapper>
	);
};

const PageWrapper = styled.div`
	width: 100%;
	min-height: 100vh;
	background: ${colors.grey5};
`;

const HeaderSection = styled.div`
	max-width: 1200px;
	margin: 0 auto;
	padding: 32px 24px 24px 24px;

	@media ${physioMeTabletBreakpoint} {
		padding: 24px 20px 20px 20px;
	}

	@media (max-width: 520px) {
		padding: 20px 16px 16px 16px;
	}
`;

const BackButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	color: ${colors.primary};
	background: transparent;
	border: none;
	padding: 8px 0;
	font-family: ${fonts.body};
	transition: color 0.2s ease;
	margin-bottom: 16px;

	&:hover {
		color: ${colors.primaryDark};
	}

	&:focus {
		outline: 2px solid ${colors.primary};
		outline-offset: 2px;
		border-radius: 4px;
	}

	&:active {
		transform: scale(0.98);
	}

	svg {
		flex-shrink: 0;
		path {
			fill: currentColor;
		}
	}
`;

const BackLabel = styled.span`
	font-family: ${fonts.body};
	font-size: 16px;
	font-weight: 600;
	line-height: 1.5;
	color: ${colors.primary};
`;

const PageTitle = styled.h1`
	font-family: ${fonts.header};
	font-size: 32px;
	font-weight: 700;
	color: ${colors.black};
	margin: 0;

	@media (max-width: 520px) {
		font-size: 28px;
	}
`;

const ContentContainer = styled.div`
	max-width: 1200px;
	margin: 0 auto;
	padding: 0 24px 48px 24px;

	@media ${physioMeTabletBreakpoint} {
		padding: 0 20px 40px 20px;
	}

	@media (max-width: 520px) {
		padding: 0 16px 32px 16px;
	}
`;

const ContentCard = styled.div`
	background: ${colors.white};
	border-radius: 16px;
	padding: 64px 120px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

	@media ${physioMeTabletBreakpoint} {
		padding: 48px;
		border-radius: 12px;
	}

	@media (max-width: 520px) {
		padding: 24px;
		border-radius: 12px;
	}
`;

const LoadingMessage = styled.div`
	width: 100%;
	text-align: center;
	padding: 80px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
`;

const LoadingSpinner = styled.div`
	width: 40px;
	height: 40px;
	border: 4px solid ${colors.primary25};
	border-top-color: ${colors.primary};
	border-radius: 50%;
	animation: spin 0.8s linear infinite;

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
`;

const LoadingText = styled.p`
	font-family: ${fonts.body};
	font-size: 16px;
	color: ${colors.grey50};
	margin: 0;
`;

const ErrorMessage = styled.div`
	width: 100%;
	max-width: 500px;
	margin: 40px auto;
	padding: 32px;
	background: ${colors.rejection10};
	border: 1px solid ${colors.rejection25};
	border-radius: 12px;
	text-align: center;
`;

const ErrorTitle = styled.h2`
	font-family: ${fonts.header};
	font-size: 20px;
	font-weight: 700;
	color: ${colors.rejectionDark};
	margin: 0 0 12px 0;
`;

const ErrorText = styled.p`
	font-family: ${fonts.body};
	font-size: 14px;
	color: ${colors.rejectionDark};
	margin: 0 0 20px 0;
	line-height: 1.5;
`;

const RetryButton = styled.button`
	padding: 10px 24px;
	background: ${colors.primary};
	color: ${colors.white};
	border: none;
	border-radius: 8px;
	font-family: ${fonts.body};
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: ${colors.primaryDark};
		transform: translateY(-1px);
	}

	&:focus {
		outline: 2px solid ${colors.primary};
		outline-offset: 2px;
	}

	&:active {
		transform: translateY(0);
	}
`;

export default LearnDocumentPage;
